import React from 'react';
import { Link } from 'react-router-dom';
import MainNavigation from '../components/common/MainNavigation';
import ScrollReveal from '../components/common/ScrollReveal';
import TeslaButton from '../components/common/TeslaButton';
import SiteFooter from '../components/common/SiteFooter';
import { ABOUT_PAGE_IMAGES } from '../constants/marketingPageImages';
import StaticPageSeo from '../seo/StaticPageSeo';

const AboutPage: React.FC = () => {
  return (
    <>
      <StaticPageSeo path="/about" />
      {/* Main Navigation */}
      <MainNavigation variant="solid" />
      
      <div className="about-page">
        {/* Hero Section */}
        <section className="section section--hero section--dark">
          <div className="container">
            <div className="row align-items-center min-vh-100">
              <div className="col-lg-6">
                <ScrollReveal animation="fade-in-up">
                  <h1 className="text-heading-1 mb-6 text-white">
                    Revolutionizing the Beauty Industry
                  </h1>
                  <p className="text-body-large mb-8 text-gray-40">
                    LookBel was born from a simple idea: beauty appointments shouldn't be complicated. 
                    We're connecting talented stylists with clients who value convenience, quality, and trust.
                  </p>
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <TeslaButton variant="primary" size="lg">
                      Join Our Community
                    </TeslaButton>
                  </Link>
                </ScrollReveal>
              </div>
              <div className="col-lg-6">
                <ScrollReveal animation="fade-in-left" delay={200}>
                  <div className="hero-image">
                    <img 
                      src={ABOUT_PAGE_IMAGES.hero}
                      alt="Professional beauty salon environment"
                      className="img-fluid rounded-lg"
                      draggable={false}
                      onContextMenu={(event) => event.preventDefault()}
                    />
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="section section--light">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center">
                <ScrollReveal animation="fade-in-up">
                  <h2 className="text-heading-2 mb-6">Our Mission</h2>
                  <p className="text-body-large mb-12">
                    To empower beauty professionals and simplify the client experience through innovative technology, 
                    creating a seamless bridge between talent and those who seek exceptional beauty services.
                  </p>
                </ScrollReveal>
              </div>
            </div>
            
            <div className="row">
              {missionValues.map((value, index) => (
                <div key={index} className="col-md-4 mb-8">
                  <ScrollReveal animation="fade-in-up" delay={200 * index}>
                    <div className="tesla-card p-8 h-100">
                      <div className="mb-4 text-accent" style={{ fontSize: '3rem' }}>
                        {value.icon}
                      </div>
                      <h3 className="text-heading-3 mb-4">{value.title}</h3>
                      <p className="text-body-medium">{value.description}</p>
                    </div>
                  </ScrollReveal>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="section section--center">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-8 mb-lg-0">
                <ScrollReveal animation="fade-in-right">
                  <img 
                    src={ABOUT_PAGE_IMAGES.ourStory}
                    alt="Team collaboration"
                    className="img-fluid rounded-lg"
                    draggable={false}
                    onContextMenu={(event) => event.preventDefault()}
                  />
                </ScrollReveal>
              </div>
              <div className="col-lg-6">
                <ScrollReveal animation="fade-in-left">
                  <h2 className="text-heading-2 mb-6">Our Story</h2>
                  <p className="text-body-medium mb-6">
                    Founded in 2024, LookBel emerged from the frustration of endless phone calls, 
                    missed appointments, and the disconnect between talented stylists and clients seeking quality services.
                  </p>
                  <p className="text-body-medium mb-6">
                    Our founders, having experienced both sides of the beauty industry, recognized the need for a 
                    platform that would streamline bookings, enhance communication, and elevate the overall experience 
                    for everyone involved.
                  </p>
                  <p className="text-body-medium">
                    Today, we're proud to serve thousands of stylists and clients across the country, 
                    making beauty appointments as simple as a few taps on your phone.
                  </p>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="section section--dark">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center mb-12">
                <ScrollReveal animation="fade-in-up">
                  <h2 className="text-heading-2 mb-6 text-white">LookBel by the Numbers</h2>
                  <p className="text-body-large text-gray-40">
                    Our impact on the beauty industry continues to grow every day.
                  </p>
                </ScrollReveal>
              </div>
            </div>
            
            <div className="row">
              {stats.map((stat, index) => (
                <div key={index} className="col-lg-3 col-md-6 mb-8">
                  <ScrollReveal animation="fade-in-up" delay={200 * index}>
                    <div className="text-center">
                      <div className="text-accent mb-4" style={{ fontSize: '4rem', fontWeight: 'bold' }}>
                        {stat.number}
                      </div>
                      <h3 className="text-heading-3 mb-2 text-white">{stat.label}</h3>
                      <p className="text-body-medium text-gray-40">{stat.description}</p>
                    </div>
                  </ScrollReveal>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section section--center section--light">
          <div className="container text-center">
            <ScrollReveal animation="fade-in-up">
              <h2 className="text-heading-2 mb-6">Ready to Join the Revolution?</h2>
              <p className="text-body-large mb-8">
                Whether you're a talented stylist or someone looking for exceptional beauty services, 
                LookBel is your gateway to a better experience.
              </p>
              <div className="d-flex justify-content-center gap-4">
                <Link to="/register" style={{ textDecoration: 'none' }}>
                  <TeslaButton variant="primary" size="lg">
                    Get Started Today
                  </TeslaButton>
                </Link>
                <Link to="/services" style={{ textDecoration: 'none' }}>
                  <TeslaButton variant="outline" size="lg">
                    Explore Services
                  </TeslaButton>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <SiteFooter />
      </div>
    </>
  );
};

// Mission values data
const missionValues = [
  {
    icon: '🎯',
    title: 'Excellence',
    description: 'We connect clients with top-tier beauty professionals who are passionate about their craft and committed to delivering exceptional results.'
  },
  {
    icon: '⚡',
    title: 'Convenience',
    description: 'Streamlined booking, flexible scheduling, and mobile services that fit seamlessly into your busy lifestyle.'
  },
  {
    icon: '🤝',
    title: 'Trust',
    description: 'Building lasting relationships through verified professionals, transparent pricing, and reliable service delivery.'
  }
];

// Stats data
const stats = [
  {
    number: '10K+',
    label: 'Active Stylists',
    description: 'Verified professionals across the country'
  },
  {
    number: '50K+',
    label: 'Happy Clients',
    description: 'Satisfied customers and growing'
  },
  {
    number: '100K+',
    label: 'Appointments',
    description: 'Successfully completed bookings'
  },
  {
    number: '4.9★',
    label: 'Average Rating',
    description: 'Based on client reviews'
  }
];

export default AboutPage; 